import React, { useContext, useState } from "react";
import { Button } from "@mui/material";
import { getDocs } from "firebase/firestore";
import jsPDF from "jspdf";
import autoTable from "jspdf-autotable";
import PropertyContext from "../../contexts/PropertyContext";
import { propertyCollection } from "../../utils/firestorePaths";
import { drawReportHeader, stampPageNumbers } from "../../utils/reports/pdfChrome";
import { downloadBlob } from "../../utils/reports/downloadBlob";
import { buildReportFilename } from "../../utils/reports/reportFilename";
import { fmtUSD, toDate } from "../../utils/reports/format";
import ReportDateRangeDialog from "./shared/ReportDateRangeDialog";

/**
 * PO Spend by Vendor — purchase orders created in the date range, aggregated by
 * vendor: PO count, expected total, and actual total (where recorded).
 */
export default function GeneratePOSpendByVendor({ user }) {
  const { selectedProperty, selectedClient } = useContext(PropertyContext);
  const [open, setOpen] = useState(false);
  const [isGenerating, setIsGenerating] = useState(false);

  const handleGenerate = async ({ start, end }) => {
    setIsGenerating(true);
    try {
      const ref = propertyCollection(selectedClient, selectedProperty, "PORs");
      const snap = await getDocs(ref);

      const byVendor = new Map(); // vendor -> { count, expected, actual }
      snap.docs.forEach((d) => {
        const po = d.data();
        const when = toDate(po.createdAt || po.quoteRequestedAt || po.date);
        if (!when || when < start || when > end) return;
        const v = po.vendor || "—";
        const agg = byVendor.get(v) || { count: 0, expected: 0, actual: 0 };
        agg.count += 1;
        agg.expected += Number(po.expectedTotal) || 0;
        agg.actual += Number(po.actualTotal) || 0;
        byVendor.set(v, agg);
      });

      if (byVendor.size === 0) {
        alert("No purchase orders found in that date range.");
        return;
      }

      const rows = Array.from(byVendor.entries()).sort(
        (a, b) => b[1].expected - a[1].expected
      );
      const totals = rows.reduce(
        (acc, [, v]) => ({
          count: acc.count + v.count,
          expected: acc.expected + v.expected,
          actual: acc.actual + v.actual,
        }),
        { count: 0, expected: 0, actual: 0 }
      );

      const pdf = new jsPDF({ orientation: "portrait", unit: "mm", format: "a4" });
      const contentTop = drawReportHeader(pdf, {
        title: "PO Spend by Vendor",
        dateRange: { start, end },
        generatedBy: user?.fullName || user?.personalEmail || "Unknown",
      });

      const body = rows.map(([vendor, v]) => [
        vendor,
        String(v.count),
        fmtUSD(v.expected),
        fmtUSD(v.actual),
      ]);
      body.push([
        { content: "TOTAL", styles: { fontStyle: "bold" } },
        { content: String(totals.count), styles: { fontStyle: "bold", halign: "right" } },
        { content: fmtUSD(totals.expected), styles: { fontStyle: "bold", halign: "right" } },
        { content: fmtUSD(totals.actual), styles: { fontStyle: "bold", halign: "right" } },
      ]);

      autoTable(pdf, {
        startY: contentTop,
        head: [["Vendor", "POs", "Expected", "Actual"]],
        body,
        styles: { font: "helvetica", fontSize: 10, cellPadding: 3 },
        headStyles: { fillColor: [40, 40, 40], textColor: 255 },
        columnStyles: {
          1: { halign: "right" },
          2: { halign: "right" },
          3: { halign: "right" },
        },
        margin: { left: 12, right: 12 },
        theme: "striped",
      });

      stampPageNumbers(pdf);
      downloadBlob(
        pdf.output("blob"),
        buildReportFilename("PO_Spend_By_Vendor", { ext: "pdf", start, end })
      );
      setOpen(false);
    } catch (e) {
      console.error("Error generating PO Spend by Vendor:", e);
      alert("Error generating report. See console for details.");
    } finally {
      setIsGenerating(false);
    }
  };

  return (
    <>
      <Button variant="contained" fullWidth onClick={() => setOpen(true)}>
        PO Spend by Vendor
      </Button>
      <ReportDateRangeDialog
        open={open}
        title="PO Spend by Vendor"
        description="Purchase orders created in the range, grouped by vendor (expected vs. actual)."
        busy={isGenerating}
        onClose={() => setOpen(false)}
        onSubmit={handleGenerate}
      />
    </>
  );
}
